import { useState } from 'react';
import {
  AddressField,
  Alert,
  Button,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  SearchSelect,
  type Address,
  type FormSectionStatus,
} from '@evogard/ui';
import type { AddressWrite } from '@evogard/contract';
import {
  COMPANY_TYPES_BY_CATEGORY,
  formatDocument,
  formatPhone,
  isValidCnpj,
  isValidCpf,
  normalizePhone,
  type CompanyCategory,
  type CompanyType,
} from '@evogard/domain';
import { geocodeAddress } from '../../../lib/geocode.js';
import {
  CATEGORY_COPY,
  COMPANY_TYPE_LABELS,
  useCompanyMutations,
  type Company,
} from './useCompanies.js';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Cadastrar ou corrigir um prestador ou uma instituição — a mesma tabela
 * (`companies`), com o formulário que cada grupo pede:
 *
 * - **Prestador** (oficina, prestador de serviços, fornecedor de peças): nome,
 *   CPF ou CNPJ — um campo só, e o número de dígitos diz qual é —, telefone e
 *   e-mail, os dois opcionais;
 * - **Instituição** (instituição financeira, órgão público): nome e CNPJ, e
 *   mais nada (decidido em 25/09). A API também recusa CPF aqui.
 * - **O endereço do prestador** (07/10/2026), opcional: o mesmo campo do
 *   aviso — o CEP preenche o resto, e o pino no mapa vira link. A oficina
 *   aparece com ele na mesa do Reparo.
 *
 * O dígito verificador é conferido na tela, antes de enviar. A forma é a dos
 * formulários do sinistro (README de `claims`): uma seção com ícone e selo, e o
 * `status` sai da mesma lista `required` que decide o envio. `nested` porque
 * abre também dentro do painel de ações da tabela.
 */
export function CompanyForm({
  category,
  editing,
  onSaved,
  onCancel,
}: {
  category: CompanyCategory;
  /** Presente: corrige este cadastro. Ausente: cadastra um novo. */
  editing?: Company;
  onSaved: (company: Company) => void;
  onCancel: () => void;
}) {
  const copy = CATEGORY_COPY[category];
  const types = COMPANY_TYPES_BY_CATEGORY[category];
  const institution = category === 'instituicoes';
  const { create, update } = useCompanyMutations();
  const mutation = editing ? update : create;

  const [type, setType] = useState<CompanyType | ''>(
    (editing?.type as CompanyType | undefined) ?? (types.length === 1 ? types[0]! : ''),
  );
  const [name, setName] = useState(editing?.name ?? '');
  const [document, setDocument] = useState(
    editing ? formatDocument(editing.document) : '',
  );
  const [phone, setPhone] = useState(editing?.phone ? formatPhone(editing.phone) : '');
  const [whatsapp, setWhatsapp] = useState(
    editing?.whatsapp ? formatPhone(editing.whatsapp) : '',
  );
  const [email, setEmail] = useState(editing?.email ?? '');
  const [address, setAddress] = useState<Partial<Address>>(() =>
    addressFrom(editing?.address ?? null),
  );
  const [touched, setTouched] = useState(false);

  const digits = document.replace(/\D/g, '');
  /* instituição é sempre CNPJ; prestador é o que o número de dígitos disser */
  const documentType: 'cpf' | 'cnpj' = institution || digits.length > 11 ? 'cnpj' : 'cpf';
  const documentValid =
    documentType === 'cnpj' ? isValidCnpj(digits) : isValidCpf(digits);
  const phoneValid = phone.trim() === '' || normalizePhone(phone) !== null;
  const whatsappValid = whatsapp.trim() === '' || normalizePhone(whatsapp) !== null;
  const emailValid = email.trim() === '' || EMAIL.test(email.trim());

  const documentLabel = institution ? 'CNPJ' : 'CPF ou CNPJ';

  /* Os obrigatórios — a mesma lista decide o selo, o rodapé e o envio. */
  const required: Record<string, boolean> = {
    'o tipo': type !== '',
    'o nome': name.trim() !== '',
    [`um ${documentLabel} válido`]: documentValid,
  };
  const missing = Object.keys(required).filter((label) => !required[label]);
  const status: FormSectionStatus = {
    done: Object.keys(required).length - missing.length,
    total: Object.keys(required).length,
    missing,
  };
  const canSubmit =
    missing.length === 0 &&
    phoneValid &&
    whatsappValid &&
    emailValid &&
    !mutation.isPending;

  const submit = () => {
    const body = {
      type: type as CompanyType,
      name: name.trim(),
      documentType,
      document: digits,
      ...(institution
        ? {}
        : {
            phone: phone.trim() === '' ? null : phone,
            whatsapp: whatsapp.trim() === '' ? null : whatsapp,
            email: email.trim() === '' ? null : email.trim(),
            /* Sem nada preenchido: nulo tira o que havia; no cadastro novo, não manda. */
            ...(addressBody(address)
              ? { address: addressBody(address) }
              : editing?.address
                ? { address: null }
                : {}),
          }),
    };
    const onSuccess = (saved: Company) => onSaved(saved);
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess });
    else create.mutate(body, { onSuccess });
  };

  return (
    <Form
      steps
      nested
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (canSubmit) submit();
      }}
    >
      {mutation.error && <Alert tone="erro">{mutation.error.message}</Alert>}

      <FormSection
        icon={copy.icon}
        status={status}
        title={
          editing ? 'Corrigir o cadastro' : institution ? 'A instituição' : 'O prestador'
        }
        descricao={
          institution
            ? 'Instituição é cadastrada só com nome e CNPJ.'
            : 'Oficina, prestador de serviços ou fornecedor de peças — pessoa física ou jurídica.'
        }
      >
        <FormRow cols={[12]}>
          <SearchSelect
            label="Tipo"
            required
            placeholder="Escolha o tipo…"
            options={types.map((t) => ({ value: t, label: COMPANY_TYPE_LABELS[t] }))}
            value={type}
            onChange={(next) => setType(next as CompanyType | '')}
            error={touched && type === '' ? 'Escolha o tipo.' : null}
          />
        </FormRow>

        <FormRow cols={[12]}>
          <Field
            label="Nome"
            required
            autoFocus
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              institution ? 'Razão social ou nome do órgão' : 'Como é conhecido'
            }
            error={touched && name.trim() === '' ? 'Informe o nome.' : null}
          />
        </FormRow>

        <FormRow cols={institution ? [12] : [6, 6]}>
          <Field
            label={documentLabel}
            required
            mask={institution ? 'cnpj' : 'document'}
            inputMode="numeric"
            autoComplete="off"
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            onBlur={() => setTouched(true)}
            error={
              touched && !documentValid
                ? digits === ''
                  ? `Informe o ${documentLabel}.`
                  : `${documentType === 'cnpj' ? 'CNPJ' : 'CPF'} inválido: confira os dígitos.`
                : null
            }
            valid={documentValid ? (documentType === 'cnpj' ? 'CNPJ' : 'CPF') : undefined}
          />
          {!institution && (
            <Field
              label="Telefone"
              type="tel"
              mask="phone"
              autoComplete="off"
              prefix="+55"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!phoneValid ? 'DDD e número, com 10 ou 11 dígitos.' : null}
            />
          )}
        </FormRow>

        {!institution && (
          <FormRow cols={[6, 6]}>
            <Field
              label="WhatsApp"
              type="tel"
              mask="phone"
              autoComplete="off"
              prefix="+55"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              error={!whatsappValid ? 'DDD e número, com 10 ou 11 dígitos.' : null}
              help="Para onde a Assistência 24h manda o script e o link do serviço."
            />
            <Field
              label="E-mail"
              type="email"
              autoComplete="off"
              maxLength={200}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!emailValid ? 'E-mail inválido.' : null}
            />
          </FormRow>
        )}
      </FormSection>

      {!institution && (
        <FormSection
          icon="mapa"
          title="Endereço"
          /* Nada aqui é obrigatório: a seção não trava o envio. */
          status={{ done: 0, total: 0 }}
          descricao="Opcional. Na oficina, é o que a mesa do Reparo mostra junto do contato."
        >
          <AddressField
            title="Endereço do prestador"
            initialValue={addressFrom(editing?.address ?? null)}
            onChange={setAddress}
            geocode={geocodeAddress}
          />
        </FormSection>
      )}

      <FormFooter>
        <Button type="button" variant="discreto" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
          {editing ? 'Salvar correção' : `Cadastrar ${copy.singular}`}
        </Button>
      </FormFooter>
    </Form>
  );
}

/** O endereço gravado, na forma do `AddressField`. */
function addressFrom(a: Company['address']): Partial<Address> {
  if (!a) return {};
  return {
    cep: a.zipCode ?? '',
    uf: a.state ?? '',
    localidade: a.city ?? '',
    bairro: a.neighborhood ?? '',
    logradouro: a.street ?? '',
    numero: a.number ?? '',
    complemento: a.complement ?? '',
    latitude: a.latitude,
    longitude: a.longitude,
  };
}

/** O endereço como a API o recebe; `null` quando nenhum campo foi preenchido. */
function addressBody(a: Partial<Address>): AddressWrite | null {
  const latitude = a.latitude ?? null;
  const longitude = a.longitude ?? null;
  const body: AddressWrite = {
    zipCode: a.cep?.replace(/\D/g, '') || null,
    state: a.uf?.trim() || null,
    city: a.localidade?.trim() || null,
    neighborhood: a.bairro?.trim() || null,
    street: a.logradouro?.trim() || null,
    number: a.numero?.trim() || null,
    complement: a.complemento?.trim() || null,
    referencePoint: null,
    locationLink:
      latitude !== null && longitude !== null
        ? `https://www.google.com/maps?q=${latitude},${longitude}`
        : null,
    latitude,
    longitude,
  };
  return Object.values(body).some((v) => v !== null && v !== '') ? body : null;
}
